import {
  Component,
  computed,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { NgOtpInputComponent, NgOtpInputModule } from 'ng-otp-input';
import { GoToStep } from '../../../store/onboarding/onboarding.actions';
import { OnboardingSteps } from '../../../store/onboarding/onboarding.model';
import { RegisterService } from '../../../_services/register-service/register.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { FormDirectivesService } from '../../../shared/directives/dynamic-form-directive/form-directives.service';
import { ResendBtnComponent } from '../../../shared/components/resend-btn/resend-btn.component';
import { AppSelectors } from '../../../store/app/app.selectors';
import { ProfileDropdownComponent } from '../../../shared/components/profile-dropdown/profile-dropdown.component';
import { Observable, Subscription } from 'rxjs';
import { EmployeeRoleType } from '../../../_interfaces/employee.interface';
import { DetailResponse } from '../../../_interfaces/verify-account';
import { SetUserStateFromPhoneVerified } from '../../../store/app/app.actions';
import { ProgressBarService } from '../../../_services/progress-bar-service/progress-bar.service';
import { handleOtpError } from '../../../shared/utils/general-utils';
import { otpConfig } from '../../../shared/configs/configs';

@Component({
  selector: 'app-verify-phone',
  imports: [NgOtpInputModule, FormsModule, ResendBtnComponent, ProfileDropdownComponent],
  templateUrl: './verify-phone.component.html',
})
export class VerifyPhoneComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly registerService = inject(RegisterService);
  readonly router = inject(Router);
  readonly formDirectives = inject(FormDirectivesService);
  readonly progressBarService = inject(ProgressBarService);

  protected title: string[] = ['Verify', 'phone number'];
  protected subtitle: string = 'To continue, please verify your phone number.';
  protected formLabel: WritableSignal<string> = signal('signup');

  protected error?: string;
  protected otpCode: string = '';
  protected btnDisabled: boolean = true;
  protected phone: WritableSignal<string> = signal('');
  protected resend$?: Observable<DetailResponse> = this.registerService.requestOTP();
  protected subscriptions: Subscription = new Subscription();
  protected redirectUrl: WritableSignal<string> = signal('/sign-up/subscription-selection');
  protected redirectRoleType?: EmployeeRoleType;

  @ViewChild(NgOtpInputComponent, { static: false }) ngOtpInput?: NgOtpInputComponent;

  public isEmployeeVerify: Signal<boolean> = computed<boolean>(() => this.router.url.includes('employee'));
  public otpConfig = otpConfig;

  constructor() {
    this.setup();
  }

  ngOnInit() {
    this.requestOTP();
    if (this.isEmployeeVerify()) {
      this.progressBarService.setHideProgress(true);
      this.formLabel.set('employeeSignup');
      this.redirectUrl.set('sign-up/employee/success');
      this.redirectRoleType = EmployeeRoleType.ADMIN;
    }
  }

  setup(): void {
    this.store.dispatch(new GoToStep(OnboardingSteps.VERIFY_PHONE));
    this.subscriptions.add(
      this.store.select(AppSelectors.userPhone).subscribe({
        next: (phone) => {
          if (phone) {
            this.phone.set(phone);
          }
        },
      }),
    );
  }

  requestOTP() {
    this.subscriptions.add(this.registerService.requestOTP().subscribe());
  }

  onOtpChange(otp: string) {
    this.otpCode = otp;
    this.btnDisabled = this.otpCode?.length != 6;
  }

  verifyPhone() {
    this.registerService.verifyPhone(this.otpCode).subscribe({
      next: () => {
        this.handleSuccess();
      },
      error: (error: HttpErrorResponse) => {
        this.error = handleOtpError(error);
        this.ngOtpInput?.setValue('');
      },
    });
  }

  handleSuccess(): void {
    this.store.dispatch(SetUserStateFromPhoneVerified).subscribe(() => {
      let queryParams;
      if (this.redirectRoleType) queryParams = { type: this.redirectRoleType };
      this.router.navigate([this.redirectUrl()], { queryParams: queryParams });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.isEmployeeVerify()) this.progressBarService.setHideProgress(false);
  }
}
